/**
 * Case Study & High-Res Image Lightbox & Zoom Viewer
 * Supports Full-Page Website Scroll Mode (tall mockups),
 * Interactive Zoom (up to 300%), 1:1 Pixel Inspection, and Drag-to-Pan.
 */

class CaseStudyLightbox {
    constructor() {
        this.lightboxEl = null;
        this.imgEl = null;
        this.viewportEl = null;
        this.stageEl = null;
        this.titleEl = null;
        this.zoomBadgeEl = null;
        this.loaderEl = null;
        this.openTabLink = null;
        
        this.scale = 1;
        this.minScale = 0.3;
        this.maxScale = 3.5;
        this.isOpen = false;
        this.isTall = false;
        this.naturalWidth = 0;
        this.naturalHeight = 0;
        this.viewMode = 'scroll'; // 'scroll' (natural page scroll) or 'pan' (free transform)

        // Panning variables
        this.isDragging = false;
        this.startX = 0;
        this.startY = 0;
        this.scrollLeft = 0;
        this.scrollTop = 0;

        this.init();
    }

    init() {
        this.createLightboxDOM();
        this.bindGlobalTriggers();
        this.bindEvents();
    }

    createLightboxDOM() {
        if (document.getElementById('cs-lightbox-modal')) return;

        const markup = `
            <div id="cs-lightbox-modal" class="cs-lightbox" aria-hidden="true" role="dialog" aria-label="Image Zoom Lightbox">
                <div class="cs-lightbox__backdrop"></div>
                
                <div class="cs-lightbox__header">
                    <div class="cs-lightbox__info">
                        <span class="cs-lightbox__tag">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            High-Res Design
                        </span>
                        <h4 class="cs-lightbox__title">Image Preview</h4>
                    </div>

                    <div class="cs-lightbox__toolbar">
                        <span class="cs-lightbox__zoom-level" title="Current Zoom">Fit Width</span>

                        <button type="button" class="cs-lightbox__btn" data-action="fit-width" title="Full Page / Readable Width (Fit Width)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M4 12h16M4 12l4-4m-4 4l4 4M20 12l-4-4m4 4l-4 4"/>
                            </svg>
                        </button>
                        
                        <button type="button" class="cs-lightbox__btn" data-action="zoom-in" title="Zoom In (+)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                <line x1="11" y1="8" x2="11" y2="14"></line>
                                <line x1="8" y1="11" x2="14" y2="11"></line>
                            </svg>
                        </button>

                        <button type="button" class="cs-lightbox__btn" data-action="zoom-out" title="Zoom Out (-)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                <line x1="8" y1="11" x2="14" y2="11"></line>
                            </svg>
                        </button>

                        <button type="button" class="cs-lightbox__btn" data-action="actual-size" title="1:1 Actual Resolution (100%)">
                            <span class="btn-text-badge">1:1</span>
                        </button>

                        <button type="button" class="cs-lightbox__btn" data-action="fit-all" title="Fit Entire Image (0)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                            </svg>
                        </button>

                        <a href="#" target="_blank" rel="noopener" class="cs-lightbox__btn cs-lightbox__btn--link" data-action="open-tab" title="Open Original High-Res Image in New Tab">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                        </a>

                        <button type="button" class="cs-lightbox__btn cs-lightbox__btn--close" data-action="close" title="Close (Esc)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>

                <div class="cs-lightbox__body">
                    <div class="cs-lightbox__loader">
                        <div class="cs-spinner"></div>
                        <span>Loading High-Resolution Design...</span>
                    </div>

                    <div class="cs-lightbox__viewport mode-fit-width">
                        <div class="cs-lightbox__stage">
                            <img class="cs-lightbox__img" src="" alt="High-Res Preview" draggable="false">
                        </div>
                    </div>
                </div>

                <div class="cs-lightbox__footer">
                    <span class="cs-lightbox__hint">
                        💡 <strong>Viewing Tip:</strong> Scroll naturally to browse full page &bull; Use <kbd>+</kbd> / <kbd>-</kbd> or buttons to zoom &bull; Click <kbd>1:1</kbd> for pixel-sharp view &bull; <kbd>Esc</kbd> to exit
                    </span>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML('beforeend', markup);

        this.lightboxEl = document.getElementById('cs-lightbox-modal');
        this.imgEl = this.lightboxEl.querySelector('.cs-lightbox__img');
        this.stageEl = this.lightboxEl.querySelector('.cs-lightbox__stage');
        this.viewportEl = this.lightboxEl.querySelector('.cs-lightbox__viewport');
        this.loaderEl = this.lightboxEl.querySelector('.cs-lightbox__loader');
        this.titleEl = this.lightboxEl.querySelector('.cs-lightbox__title');
        this.zoomBadgeEl = this.lightboxEl.querySelector('.cs-lightbox__zoom-level');
        this.openTabLink = this.lightboxEl.querySelector('.cs-lightbox__btn--link');
    }

    bindGlobalTriggers() {
        // Disabled in favor of standard glightbox modal
    }

    openFromElement(el) {
        let src = el.getAttribute('data-full-img');
        if (!src) {
            const img = el.querySelector('img') || (el.tagName.toLowerCase() === 'img' ? el : null);
            if (img) {
                src = img.getAttribute('data-full-img') || img.currentSrc || img.src;
            }
        }

        if (!src) return;

        let title = el.getAttribute('data-title');
        if (!title) {
            const img = el.querySelector('img');
            title = (img && (img.getAttribute('alt') || img.getAttribute('title'))) || document.title.split(' - ')[0] || 'Design Showcase';
        }

        this.open(src, title);
    }

    open(src, title = 'Design Showcase') {
        if (!this.lightboxEl) this.createLightboxDOM();

        this.isOpen = true;
        this.titleEl.textContent = title;
        this.openTabLink.href = src;
        this.loaderEl.classList.add('is-active');
        this.imgEl.style.opacity = '0';
        this.lightboxEl.classList.add('is-open');
        this.lightboxEl.setAttribute('aria-hidden', 'false');
        document.body.classList.add('cs-lightbox-locked');

        // Preload image to get natural dimensions
        const tempImg = new Image();
        tempImg.onload = () => {
            this.naturalWidth = tempImg.naturalWidth;
            this.naturalHeight = tempImg.naturalHeight;
            this.isTall = (this.naturalHeight / this.naturalWidth) > 1.2;

            this.imgEl.src = src;
            this.loaderEl.classList.remove('is-active');
            this.imgEl.style.opacity = '1';

            // Set initial view mode:
            // If it's a tall mockup or full-page website screenshot -> open in Full Page Scroll (Fit Width)
            if (this.isTall) {
                this.setFitWidth();
            } else {
                this.setFitAll();
            }

            // Scroll viewport to top
            this.viewportEl.scrollTop = 0;
            this.viewportEl.scrollLeft = 0;
        };

        tempImg.onerror = () => {
            this.loaderEl.classList.remove('is-active');
            this.imgEl.src = src;
            this.imgEl.style.opacity = '1';
            this.setFitWidth();
        };

        tempImg.src = src;
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.lightboxEl.classList.remove('is-open');
        this.lightboxEl.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cs-lightbox-locked');

        setTimeout(() => {
            if (!this.isOpen && this.imgEl) {
                this.imgEl.src = '';
            }
        }, 300);
    }

    setFitWidth() {
        this.viewMode = 'fit-width';
        this.viewportEl.className = 'cs-lightbox__viewport mode-fit-width';
        
        const vpWidth = this.viewportEl.clientWidth;
        const targetWidth = Math.max(900, vpWidth - 60);
        
        this.imgEl.style.width = `${targetWidth}px`;
        this.imgEl.style.maxWidth = '100%';
        this.imgEl.style.height = 'auto';
        this.imgEl.style.maxHeight = 'none';
        this.stageEl.style.transform = 'none';

        const naturalW = this.naturalWidth || 1200;
        this.scale = targetWidth / naturalW;
        const pct = Math.round((targetWidth / naturalW) * 100);
        this.zoomBadgeEl.textContent = `Fit Width (${pct}%)`;
    }

    setActualSize() {
        this.viewMode = 'actual';
        this.viewportEl.className = 'cs-lightbox__viewport mode-actual';
        
        this.imgEl.style.width = `${this.naturalWidth}px`;
        this.imgEl.style.maxWidth = 'none';
        this.imgEl.style.height = 'auto';
        this.imgEl.style.maxHeight = 'none';
        this.stageEl.style.transform = 'none';

        this.scale = 1.0;
        this.zoomBadgeEl.textContent = '100% (1:1)';
    }

    setFitAll() {
        this.viewMode = 'fit-all';
        this.viewportEl.className = 'cs-lightbox__viewport mode-fit-all';

        this.imgEl.style.width = 'auto';
        this.imgEl.style.maxWidth = '90vw';
        this.imgEl.style.height = 'auto';
        this.imgEl.style.maxHeight = '82vh';
        this.stageEl.style.transform = 'none';

        this.zoomBadgeEl.textContent = 'Fit All';
    }

    zoomIn() {
        let currentWidth = this.imgEl.getBoundingClientRect().width;
        let newWidth = currentWidth * 1.25;
        this.setCustomWidth(newWidth);
    }

    zoomOut() {
        let currentWidth = this.imgEl.getBoundingClientRect().width;
        let newWidth = currentWidth / 1.25;
        this.setCustomWidth(newWidth);
    }

    setCustomWidth(width) {
        this.viewMode = 'custom';
        this.viewportEl.className = 'cs-lightbox__viewport mode-custom';

        const minW = 400;
        const maxW = Math.max(this.naturalWidth * 3, 4000);
        width = Math.max(minW, Math.min(maxW, width));

        this.imgEl.style.width = `${Math.round(width)}px`;
        this.imgEl.style.maxWidth = 'none';
        this.imgEl.style.height = 'auto';
        this.imgEl.style.maxHeight = 'none';
        this.stageEl.style.transform = 'none';

        const pct = Math.round((width / (this.naturalWidth || 1200)) * 100);
        this.zoomBadgeEl.textContent = `${pct}%`;
    }

    bindEvents() {
        // Toolbar Button Clicks
        this.lightboxEl.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-action]');
            if (!btn) {
                if (e.target.classList.contains('cs-lightbox__backdrop')) {
                    this.close();
                }
                return;
            }

            const action = btn.getAttribute('data-action');
            switch (action) {
                case 'close':
                    this.close();
                    break;
                case 'fit-width':
                    this.setFitWidth();
                    break;
                case 'actual-size':
                    this.setActualSize();
                    break;
                case 'fit-all':
                    this.setFitAll();
                    break;
                case 'zoom-in':
                    this.zoomIn();
                    break;
                case 'zoom-out':
                    this.zoomOut();
                    break;
            }
        });

        // Double click image to toggle between 1:1 and Fit Width
        this.imgEl.addEventListener('dblclick', (e) => {
            e.preventDefault();
            if (this.viewMode === 'actual') {
                this.setFitWidth();
            } else {
                this.setActualSize();
            }
        });

        // Drag to Scroll / Pan inside viewport
        this.viewportEl.addEventListener('mousedown', (e) => {
            if (e.button !== 0) return;
            if (e.target.closest('.cs-lightbox__header') || e.target.closest('.cs-lightbox__footer')) return;

            this.isDragging = true;
            this.startX = e.pageX - this.viewportEl.offsetLeft;
            this.startY = e.pageY - this.viewportEl.offsetTop;
            this.scrollLeft = this.viewportEl.scrollLeft;
            this.scrollTop = this.viewportEl.scrollTop;
            this.viewportEl.style.cursor = 'grabbing';
            this.viewportEl.style.userSelect = 'none';
        });

        window.addEventListener('mousemove', (e) => {
            if (!this.isDragging) return;
            e.preventDefault();
            const x = e.pageX - this.viewportEl.offsetLeft;
            const y = e.pageY - this.viewportEl.offsetTop;
            const walkX = (x - this.startX) * 1.5;
            const walkY = (y - this.startY) * 1.5;
            this.viewportEl.scrollLeft = this.scrollLeft - walkX;
            this.viewportEl.scrollTop = this.scrollTop - walkY;
        });

        window.addEventListener('mouseup', () => {
            if (!this.isDragging) return;
            this.isDragging = false;
            this.viewportEl.style.cursor = 'default';
            this.viewportEl.style.removeProperty('user-select');
        });

        // Ctrl + Mouse Wheel for Zoom In / Out
        this.viewportEl.addEventListener('wheel', (e) => {
            if (e.ctrlKey) {
                e.preventDefault();
                if (e.deltaY < 0) {
                    this.zoomIn();
                } else {
                    this.zoomOut();
                }
            }
            // Otherwise, normal mousewheel allows smooth, natural vertical scrolling of the web page!
        }, { passive: false });

        // Keyboard Shortcuts
        window.addEventListener('keydown', (e) => {
            if (!this.isOpen) return;

            switch (e.key) {
                case 'Escape':
                    this.close();
                    break;
                case '+':
                case '=':
                    e.preventDefault();
                    this.zoomIn();
                    break;
                case '-':
                case '_':
                    e.preventDefault();
                    this.zoomOut();
                    break;
                case '1':
                    e.preventDefault();
                    this.setActualSize();
                    break;
                case 'w':
                case 'W':
                    e.preventDefault();
                    this.setFitWidth();
                    break;
                case '0':
                    e.preventDefault();
                    this.setFitAll();
                    break;
            }
        });
    }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.caseStudyLightbox = new CaseStudyLightbox();
});

export default CaseStudyLightbox;
